import React, { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { ArrowLeft, ExternalLink } from "lucide-react";
import api from "@/lib/api";
import Reveal from "@/components/Reveal";
import { StatusBadge } from "@/components/public/VentureCard";
import DiamondMark from "@/components/DiamondMark";

export default function VentureDetail() {
  const { slug } = useParams();
  const [v, setV] = useState(null);
  const [notFound, setNotFound] = useState(false);

  useEffect(() => {
    api.get(`/ventures/${slug}`).then(({ data }) => setV(data)).catch(() => setNotFound(true));
  }, [slug]);

  if (notFound) {
    return (
      <div className="bg-bd-black min-h-screen flex items-center justify-center text-center px-6">
        <div>
          <h1 className="font-serif text-4xl text-bd-ivory mb-4">Venture not found</h1>
          <Link to="/ventures" className="text-bd-gold hover:text-bd-gold2">← Back to ventures</Link>
        </div>
      </div>
    );
  }
  if (!v) return <div className="bg-bd-black min-h-screen" />;

  const accent = v.accent || "#B89C65";

  return (
    <div data-testid="page-venture-detail" className="bg-bd-black min-h-screen">
      <section className="relative pt-40 pb-20 overflow-hidden grain">
        {v.cover_url && <img src={v.cover_url} alt="" className="absolute inset-0 w-full h-full object-cover opacity-30" />}
        <div className="absolute inset-0 bg-gradient-to-t from-bd-black to-bd-black/60" />
        <div className="relative z-10 max-w-5xl mx-auto px-6 md:px-12">
          <Link to="/ventures" className="inline-flex items-center gap-2 text-bd-ivory/50 hover:text-bd-gold transition-colors text-sm mb-10">
            <ArrowLeft size={16} /> All ventures
          </Link>
          <div className="flex items-center gap-4 mb-6">
            {v.logo_url ? (
              <img src={v.logo_url} alt={v.name} className="w-16 h-16 object-contain" />
            ) : (
              <DiamondMark size={40} stroke={accent} />
            )}
            <StatusBadge status={v.status} />
          </div>
          <p className="overline mb-4" style={{ color: accent }}>{v.industry || "Venture"}</p>
          <h1 className="font-serif text-4xl md:text-7xl text-bd-ivory leading-[1.05]">{v.name}</h1>
          {v.tagline && <p className="mt-6 text-lg md:text-xl text-bd-ivory/70 max-w-2xl">{v.tagline}</p>}
        </div>
      </section>

      <section className="py-16 md:py-24 bg-bd-charcoal">
        <div className="max-w-5xl mx-auto px-6 md:px-12 grid grid-cols-1 lg:grid-cols-3 gap-12">
          <div className="lg:col-span-2">
            <Reveal>
              <p className="text-base md:text-lg text-bd-ivory/70 leading-relaxed whitespace-pre-line">
                {v.public_summary || v.tagline || "Details for this venture will be published in due course."}
              </p>
            </Reveal>
          </div>
          <div className="lg:col-span-1">
            <Reveal delay={0.1}>
              <div className="border border-white/10 p-8 space-y-6">
                <div>
                  <p className="overline text-bd-ivory/40 mb-1">Status</p>
                  <p className="text-bd-ivory">{v.status}</p>
                </div>
                <div>
                  <p className="overline text-bd-ivory/40 mb-1">Industry</p>
                  <p className="text-bd-ivory">{v.industry || "—"}</p>
                </div>
                {v.launch_date && (
                  <div>
                    <p className="overline text-bd-ivory/40 mb-1">Launch</p>
                    <p className="text-bd-ivory">{v.launch_date}</p>
                  </div>
                )}
                {v.website && (
                  <a href={v.website} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-2 text-bd-gold hover:text-bd-gold2 transition-colors text-sm">
                    Visit website <ExternalLink size={14} />
                  </a>
                )}
                <div className="pt-4 border-t border-white/10">
                  <Link to="/contact" className="block text-center border border-bd-gold text-bd-gold hover:bg-bd-gold hover:text-bd-black transition-colors duration-300 px-6 py-3 text-xs tracking-[0.15em] uppercase">
                    Enquire about this venture
                  </Link>
                </div>
              </div>
            </Reveal>
          </div>
        </div>
      </section>
    </div>
  );
}
